import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type ReportLayout } from './inspectionEndpoints';
import {
  blobOrError, revokeWithCacheEntry, toObjectUrl, type ReportImageRef, type ReportPreviewSource, type ReportText,
} from './reportDesignEndpoints';

/**
 * The layouts that print the brand: every fixed layout but `classic`, which mirrors the SuPAR
 * app's own report. The brand is the same on each, so a layout is only what a preview is
 * asked to show it on. Mirrors the layouts the backend flags `branding` in
 * `report/pdf/layouts.ts`; a preview refuses any other with 400.
 */
export const brandableLayouts = ['compact', 'executive'] as const satisfies readonly ReportLayout[];
export type BrandableLayout = typeof brandableLayouts[number];

export const isBrandableLayout = (value: unknown): value is BrandableLayout =>
  (brandableLayouts as readonly unknown[]).includes(value);

/** The texts of a brand, in the order an editor shows them. Mirrors the backend's `reportBrandingTextKeys`. */
export const reportBrandingTextKeys = ['brandName', 'headerLabel', 'footerText'] as const;
export type ReportBrandingTextKey = typeof reportBrandingTextKeys[number];

/** How long each text of a brand may get in one locale. Mirrors the backend's `reportBrandingLimits`. */
export const reportBrandingLimits: Record<ReportBrandingTextKey, number> = {
  brandName: 60,
  headerLabel: 80,
  footerText: 80,
};

/**
 * What a layer of a brand can set. A key is there only while the layer sets it: one left out
 * is the layer beneath showing through.
 */
export interface ReportBrandingFields {
  /** In place of the product's name. */
  brandName?: ReportText;
  /** The line a page's header carries, in place of "Inspection report". Printed as written. */
  headerLabel?: ReportText;
  /** In place of the footer's "Generated with ..." line. */
  footerText?: ReportText;
  /** An image uploaded to this layer's own owner. */
  logo?: ReportImageRef;
}

export const reportBrandingFieldKeys = [
  ...reportBrandingTextKeys, 'logo',
] as const satisfies readonly (keyof ReportBrandingFields)[];
export type ReportBrandingFieldKey = typeof reportBrandingFieldKeys[number];

/** Whose layer: a team's, or the organization's when `teamId` is `null`. */
export interface ReportBrandingScope {
  teamId: string | null;
}

/**
 * One layer of the brand a report is printed with: the organization's, or one team's on top
 * of it. Under both is what a layout prints by itself. A field replaces the one beneath
 * whole, a text with all its locales.
 */
export interface ReportBrandingLayer extends ReportBrandingScope, ReportBrandingFields {
  /** `null` for the organization, which has no name of its own. */
  teamName: string | null;
  /** Absent where nothing is stored: the answer to a write that emptied the layer. */
  updatedAt?: string;
}

/**
 * Every layer the reader may open, by `brandingLayerKey`. An owner nothing was set for has no
 * entry, which reads the same as a layer that sets nothing.
 */
export type ReportBrandingLayers = Record<string, ReportBrandingLayer>;

export const brandingLayerKey = ({ teamId }: ReportBrandingScope): string => teamId ?? 'organization';

/** A value sets the field, `null` takes it off so the layer beneath shows through, a key left out is not touched. */
export type ReportBrandingPatch = { [Key in ReportBrandingFieldKey]?: ReportBrandingFields[Key] | null };

export interface PatchReportBrandingInput extends ReportBrandingScope {
  patch: ReportBrandingPatch;
}

/**
 * What a preview of a brand is drawn with. `draft` is the layer as the editor holds it, whole
 * and not a patch; with it the caller needs the right to save the layer, without it the right
 * to read it, and the stored layer is drawn. Under a team's layer the server puts the
 * organization's stored one. `layout` is the layout to show the brand on, the first that
 * takes one when left out. Without `source` it is drawn over the sample inspection.
 */
export interface ReportBrandingPreviewRequest extends ReportBrandingScope {
  layout?: BrandableLayout;
  draft?: ReportBrandingFields;
  source?: ReportPreviewSource;
  locale?: string;
}

const pathOf = ({ teamId }: ReportBrandingScope): string =>
  `/v1/report-branding/${teamId === null ? 'organization' : `teams/${teamId}`}`;

const setsAnything = (layer: ReportBrandingFields): boolean => reportBrandingFieldKeys.some((key) => layer[key] !== undefined);

const toLayers = (response: ReportBrandingLayer[]): ReportBrandingLayers =>
  Object.fromEntries(response.map((layer) => [brandingLayerKey(layer), layer]));

// Writes still on their way, counted per layer, and the layers whose answers cannot be taken
// for the server's state once they are all through. As the app settings keep theirs.
const pendingWrites = new Map<string, number>();
const outOfStep = new Set<string>();

export const reportBrandingApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /** The organization's layer and that of every team the reader may open, so moving between them asks for nothing. */
    getReportBrandings: builder.query<ReportBrandingLayers, void>({
      query: () => '/v1/report-branding',
      transformResponse: toLayers,
      providesTags: ['ReportBranding'],
      onQueryStarted: handleApiResult(),
    }),
    /**
     * Save sends every unsaved change of one layer as one merge patch. Each field is written
     * on its own server-side, so two people changing different fields never undo each other
     * and no version is claimed.
     *
     * Nothing goes into the list ahead of the answer: the page shows its draft until then, so
     * a write that is refused leaves the list as it was read and the draft unsaved.
     *
     * The answer, the layer after that write, replaces the entry only when the write ran
     * alone: answers of writes that overlap may land in either order and each lacks what the
     * other set, and so may a re-read of the list that was under way when the write began.
     * Those put in only what they sent, and once they are all through the list is read again.
     */
    patchReportBranding: builder.mutation<ReportBrandingLayer, PatchReportBrandingInput>({
      query: ({ patch, ...scope }) => ({ url: pathOf(scope), method: 'PATCH', body: patch }),
      onQueryStarted: async (input, api) => {
        const { patch, ...scope } = input;
        const target = brandingLayerKey(scope);

        const pending = (pendingWrites.get(target) ?? 0) + 1;
        pendingWrites.set(target, pending);

        const isListBeingRead = reportBrandingApiSlice.endpoints.getReportBrandings.select()(api.getState()).status === 'pending';
        if (pending > 1 || isListBeingRead) outOfStep.add(target);

        await handleApiResult()(input, api);

        let answer: ReportBrandingLayer | undefined;

        try {
          answer = (await api.queryFulfilled).data;
        } catch (_) {
          // Refused, or never arrived: nothing was written, there or here.
        }

        const left = (pendingWrites.get(target) ?? 1) - 1;

        if (left > 0) pendingWrites.set(target, left);
        else pendingWrites.delete(target);

        const isAlone = left === 0 && !outOfStep.has(target);

        if (answer) {
          const settled = answer;

          api.dispatch(reportBrandingApiSlice.util.updateQueryData('getReportBrandings', undefined, (layers) => {
            const layer: ReportBrandingLayer = isAlone ? settled : layers[target] ?? { teamId: scope.teamId, teamName: settled.teamName };

            if (!isAlone) {
              for (const key of reportBrandingTextKeys) {
                const text = patch[key];

                if (text === null) delete layer[key];
                else if (text) layer[key] = text;
              }

              if (patch.logo === null) delete layer.logo;
              else if (patch.logo) layer.logo = patch.logo;
            }

            // A layer that sets nothing is not stored, and has no entry here either.
            if (setsAnything(layer)) layers[target] = layer;
            else delete layers[target];
          }));
        }

        if (left === 0 && outOfStep.delete(target)) {
          api.dispatch(reportBrandingApiSlice.util.invalidateTags(['ReportBranding']));
        }
      },
    }),
    /**
     * Stores a logo for a layer and answers the id a patch names it by. Nothing refers to it
     * yet, and the server keeps an unreferenced image for a day. A layer takes only a logo
     * that was uploaded to its own owner.
     */
    uploadReportBrandingLogo: builder.mutation<{ imageId: string }, ReportBrandingScope & { file: File }>({
      query: ({ file, ...scope }) => {
        const body = new FormData();

        body.append('file', file);

        return { url: `${pathOf(scope)}/logo`, method: 'POST', body };
      },
      onQueryStarted: handleApiResult(),
    }),
    /**
     * A logo uploaded to a layer as an address a picture can show, read with the session's
     * token as a design's image is. Whoever may read the layer may read it, whether or not
     * the layer names it: a team's manager is shown the organization's logo this way.
     *
     * No error is shown from here: a logo the server no longer has is a state of the control
     * that asked for it.
     */
    getReportBrandingLogo: builder.query<string, ReportBrandingScope & { imageId: string }>({
      query: ({ imageId, ...scope }) => ({ url: `${pathOf(scope)}/logo/${imageId}`, responseHandler: blobOrError }),
      transformResponse: toObjectUrl,
      onCacheEntryAdded: revokeWithCacheEntry,
    }),
    /**
     * Draws a brand on a layout as a PDF and answers an object URL for it, which whoever
     * asked gives back with `URL.revokeObjectURL`. A mutation that shows no error, for the
     * reasons a design's preview is one.
     */
    previewReportBranding: builder.mutation<string, ReportBrandingPreviewRequest>({
      query: ({ teamId, ...body }) => ({
        url: `${pathOf({ teamId })}/preview`,
        method: 'POST',
        body,
        responseHandler: blobOrError,
      }),
      transformResponse: toObjectUrl,
    }),
  }),
});

export const {
  useGetReportBrandingsQuery,
  useGetReportBrandingLogoQuery,
  usePatchReportBrandingMutation,
  useUploadReportBrandingLogoMutation,
  usePreviewReportBrandingMutation,
} = reportBrandingApiSlice;
